import { useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Send, MapPin, Mail, Phone } from "lucide-react";
import { API, useCart } from "../context/CartContext";

export const NewsletterFooter = () => {
  const { t } = useTranslation();
  const { config } = useCart();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success(t("newsletter.success"));
      setEmail("");
    } catch {
      toast.error(t("newsletter.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="bg-brand-yellow py-24 relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10">
          <div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-brand-dark uppercase">{t("newsletter.final")}</h2>
            <p className="mt-3 text-brand-brown font-semibold max-w-md">{t("newsletter.finalSub")}</p>
            <a href="#products" data-testid="final-cta"
              className="mt-6 inline-flex px-8 py-4 rounded-full bg-brand-dark text-brand-yellow font-bold text-sm uppercase tracking-wide hover:-translate-y-1 transition-transform">
              {t("newsletter.finalCta")}
            </a>
          </div>
          <div className="bg-white rounded-3xl p-8 shadow-xl">
            <h3 className="font-heading text-3xl text-brand-dark uppercase">{t("newsletter.title")}</h3>
            <p className="text-sm text-brand-brown mt-2">{t("newsletter.subtitle")}</p>
            <form onSubmit={subscribe} className="mt-5 flex gap-2">
              <input data-testid="newsletter-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder={t("newsletter.placeholder")}
                className="flex-1 px-5 py-3 rounded-full border border-brand-green/25 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange bg-brand-cream" />
              <button data-testid="newsletter-submit" type="submit" disabled={busy}
                className="px-5 py-3 rounded-full bg-brand-orange text-white hover:bg-brand-dark transition-colors disabled:opacity-60">
                <Send size={18} />
              </button>
            </form>
          </div>
        </div>
      </section>
      <footer className="bg-brand-dark py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="font-heading text-3xl text-brand-yellow">AGRO<span className="text-brand-orange">DYKE</span></div>
            <p className="text-sm text-white/60 mt-3 leading-relaxed max-w-xs">{t("footer.desc")}</p>
          </div>
          <div>
            <h4 className="font-heading text-xl text-white uppercase mb-4">{t("footer.links")}</h4>
            <div className="space-y-2">
              {["videos", "products", "dosage", "faq"].map((k) => (
                <a key={k} href={`#${k}`} className="block text-sm text-white/60 hover:text-brand-yellow transition-colors">
                  {t(`nav.${k}`)}
                </a>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-heading text-xl text-white uppercase mb-4">{t("footer.contact")}</h4>
            <div className="space-y-3 text-sm text-white/60">
              <div className="flex items-center gap-2"><MapPin size={16} className="text-brand-orange" /> Jakarta, Indonesia</div>
              <div className="flex items-center gap-2"><Mail size={16} className="text-brand-orange" /> office@agrodyke.com</div>
              <div className="flex items-center gap-2"><Phone size={16} className="text-brand-orange" /> +{config.whatsapp_number}</div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-12 pt-6 border-t border-white/10 flex flex-wrap justify-between gap-2 text-xs text-white/40">
          <span>© {new Date().getFullYear()} PT. Dahliah Duta Utama. {t("footer.rights")}</span>
          <span data-testid="footer-designer">Designed by Zeki Aziz</span>
        </div>
      </footer>
    </>
  );
};
